import { useRef, useEffect, useMemo } from 'react';
import { atom, useSetAtom, useAtomValue } from 'jotai';
import debounce from 'lodash/debounce';

export const trendsFilterPanelHeightAtom = atom(60);

export const useStartObservingHeight = (targetRef) => {
  const observerRef = useRef<MutationObserver | ResizeObserver>();
  const setHeight = useSetAtom(trendsFilterPanelHeightAtom);

  const debouncedSetHeight = useMemo(() => debounce((h) => {
    setHeight(h)
  }, 100), [setHeight]);

  useEffect(() => {
    if (!targetRef.current) return;
    const target = targetRef.current;

    debouncedSetHeight(target.clientHeight);

    if (!observerRef.current) {
      if ('ResizeObserver' in window) {
        observerRef.current = new ResizeObserver((entries) => {
          if (entries[0].target === target) debouncedSetHeight(entries[0].contentRect.height);
        });
        (observerRef.current as ResizeObserver).observe(target);
      } else {
        observerRef.current = new MutationObserver(() => debouncedSetHeight(target.clientHeight));
        (observerRef.current as MutationObserver).observe(target, {
          childList: true,
          subtree: true,
        });
      }
    }
    return () => {
      observerRef.current?.disconnect();
    };
  }, [targetRef.current, debouncedSetHeight]);
};

export const useTrendsFilterPanelHeight = () => useAtomValue(trendsFilterPanelHeightAtom);
